import type { Getter, Setter } from 'jotai';
import _isEmpty from 'lodash/isEmpty';
import { questionnaireDetailsMapAtom } from '../../../../../modernization-common/side-panel/atoms/questionnaire.atom';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import { updateDynamicAnswerSegments } from '../../../studio-store/utils/dynamic-answer-utils';
import { buildDefaultDynamicAnswers, findContainingSpan } from '../../../utils/frame-timeline-v4';
import { annotationMetadataAtom } from '../../annotation-atoms';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import { copiedAnnotationIdAtom, selectedAnnotationIdsAtom, showAnnotationDetailsAtom } from '../../tool-atoms';
import type { AddAnnotationEventPayload } from '../events';
export const addAnnotationCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<AddAnnotationEventPayload, void> => {
  let previousSelectedIds: string[] = [];
  return {
    execute: payload => {
      const {
        frameIndex,
        annotationIdx,
        annotationData
      } = payload;
      previousSelectedIds = get(selectedAnnotationIdsAtom);
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION,
        frameIndex,
        annotationId: annotationIdx,
        annotation: annotationData
      });
      const hasQuestionnaire = !_isEmpty(annotationData.annotationLabel?.questionnaire);
      set(selectedAnnotationIdsAtom, [annotationIdx]);
      set(showAnnotationDetailsAtom, hasQuestionnaire);
      set(copiedAnnotationIdAtom, null);
      const questionnaireId = annotationData.annotationLabel?.questionnaire?.[0]?.questionnaireId;
      const cachedQuestionnaire = questionnaireId ? get(questionnaireDetailsMapAtom)[questionnaireId] : undefined;
      const defaultAnswers = cachedQuestionnaire?.status === 'success' ? buildDefaultDynamicAnswers(cachedQuestionnaire.questionnaire) : undefined;
      const metadata = get(annotationMetadataAtom).get(annotationIdx);
      const newSpan = findContainingSpan(metadata?.spans, frameIndex);
      const existingDynamicAnswers = metadata?.dynamicAnswers ?? [];
      const hasExistingCoverage = newSpan && existingDynamicAnswers.some(segment => segment.frameSpan.start <= newSpan.end && segment.frameSpan.end >= newSpan.start);
      if (defaultAnswers && newSpan && !hasExistingCoverage) {
        const dynamicAnswers = updateDynamicAnswerSegments(existingDynamicAnswers, {
          frameSpan: newSpan,
          answers: defaultAnswers
        });
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
          annotationId: annotationIdx,
          partialPayload: {
            dynamicAnswers
          }
        });
      }
    },
    undo: payload => {
      const {
        frameIndex,
        annotationIdx
      } = payload;
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.DELETE_ANNOTATION,
        frameIndex,
        annotationId: annotationIdx
      });
      set(selectedAnnotationIdsAtom, previousSelectedIds);
    }
  };
};